import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { Store, MessageSquare, Database, ShieldCheck, AlertCircle } from "lucide-react";

function Card({ children, testId }) {
  return <div className="card-shell p-6" data-testid={testId}>{children}</div>;
}

export default function SettingsPage() {
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);

  const load = useCallback(async () => {
    const [s, p] = await Promise.all([
      api.get("/meta/integrations"),
      api.get("/meta/products"),
    ]);
    setStatus(s.data);
    setProducts(p.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!status) return <div className="p-8 text-[#71717A]">Loading…</div>;

  return (
    <div className="p-8 space-y-6 max-w-5xl">
      <div>
        <div className="data-label mb-1">Configuration</div>
        <h1 className="font-heading font-black text-4xl tracking-tight">Integrations</h1>
        <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
          Live status is pulled from each provider's health check. Shopify is wired via the client-credentials
          OAuth grant (tokens auto-refresh every 24h). WhatsApp still runs in mock mode until a Gupshup / Interakt
          key is provided.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testId="whatsapp-card">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-[#059669]" />
            <span className="data-label">WhatsApp Business API</span>
          </div>
          <h3 className="font-heading font-bold text-lg">Provider: {status.whatsapp.provider}</h3>
          <div className="mt-2 text-sm">
            {status.whatsapp.configured ? (
              <span className="inline-flex items-center gap-1 text-green-700"><ShieldCheck className="w-4 h-4" /> Live</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[#A16207]"><AlertCircle className="w-4 h-4" /> Mocked — messages logged, not sent externally</span>
            )}
          </div>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between border-b border-[#EEEEEC] pb-1.5">
              <dt className="data-label">Business number</dt>
              <dd className="font-mono">{status.whatsapp.business_number || "—"}</dd>
            </div>
          </dl>
          <div className="mt-4 p-3 rounded-md bg-[#F9F9F8] border border-[#E5E5E5] text-xs text-[#52525B]">
            To switch to Gupshup / Interakt, set <span className="font-mono">WHATSAPP_PROVIDER</span> and <span className="font-mono">WHATSAPP_API_KEY</span> in <span className="font-mono">backend/.env</span>, then restart the backend.
          </div>
        </Card>

        <Card testId="shopify-card">
          <div className="flex items-center gap-2 mb-3">
            <Store className="w-4 h-4 text-[#4F46E5]" />
            <span className="data-label">Shopify</span>
          </div>
          <h3 className="font-heading font-bold text-lg">
            {status.shopify.ping?.shop?.name || status.shopify.store_url || "Not configured"}
          </h3>
          <div className="mt-2 text-sm">
            {status.shopify.ping?.ok ? (
              <span className="inline-flex items-center gap-1 text-green-700"><ShieldCheck className="w-4 h-4" /> Live</span>
            ) : status.shopify.configured ? (
              <span className="inline-flex items-center gap-1 text-[#DC2626]"><AlertCircle className="w-4 h-4" /> Configured but unhealthy: {status.shopify.ping?.reason || "unknown"}</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[#A16207]"><AlertCircle className="w-4 h-4" /> Not configured — using mock catalog</span>
            )}
          </div>
          {status.shopify.ping?.ok && (
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between border-b border-[#EEEEEC] pb-1.5">
                <dt className="data-label">Store domain</dt>
                <dd className="font-mono text-xs">{status.shopify.ping.shop?.myshopify_domain}</dd>
              </div>
              <div className="flex justify-between border-b border-[#EEEEEC] pb-1.5">
                <dt className="data-label">Plan</dt>
                <dd className="font-mono text-xs">{status.shopify.ping.shop?.plan_name}</dd>
              </div>
              <div className="flex justify-between border-b border-[#EEEEEC] pb-1.5">
                <dt className="data-label">Granted scopes</dt>
                <dd className="font-mono text-xs">{status.shopify.ping.token_scopes || "—"}</dd>
              </div>
              <div className="flex justify-between border-b border-[#EEEEEC] pb-1.5">
                <dt className="data-label">Token expires in</dt>
                <dd className="font-mono text-xs">{Math.floor((status.shopify.ping.token_expires_in_s || 0) / 3600)}h</dd>
              </div>
              <div className="flex justify-between">
                <dt className="data-label">API version</dt>
                <dd className="font-mono text-xs">{status.shopify.api_version}</dd>
              </div>
            </dl>
          )}
          <div className="mt-4 text-xs text-[#52525B]">{status.shopify.note}</div>
        </Card>
      </div>

      <div className="card-shell overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            <span className="data-label">Product catalog (Shopify sync)</span>
          </div>
          <span className="text-xs font-mono text-[#71717A]" data-testid="products-count">
            {products.length} products
          </span>
        </div>
        <div className="scroll-shadow max-h-[520px]">
          <table className="data-table">
            <thead>
              <tr>
                <th>SKU</th><th>Category</th><th>Title</th><th>Price</th><th>Shopify ID</th>
              </tr>
            </thead>
            <tbody data-testid="products-tbody">
              {products.map((p) => (
                <tr key={p.id}>
                  <td className="font-mono text-xs">{p.sku}</td>
                  <td className="text-xs text-[#52525B]">{p.category}</td>
                  <td className="max-w-[420px] truncate" title={p.title}>{p.title}</td>
                  <td className="font-mono">{p.price != null ? `₹${Number(p.price).toLocaleString()}` : "—"}</td>
                  <td className="font-mono text-xs text-[#71717A] truncate max-w-[220px]" title={p.id}>{p.id}</td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr><td colSpan={5} className="text-center text-[#71717A] p-8 text-sm">No products returned from Shopify.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
